import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash';
import { faBooks } from '@fortawesome/pro-duotone-svg-icons';
import { faBooks as faBooksSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { MAX_CONTRIBUTORS_TO_DISPLAY } from '@app/components/Browse/constants';
import { DelimitedList, EmptyRecordsMessage } from '@app/components';
import { useQueryParams } from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { ROUTE } from '@app/constants';

import { Browse, BrowseColumn } from '../Browse';
import { callNumbersMessages } from './callNumbersMessages';
import { useCallNumbersFetch } from './useCallNumbersFetch';
import { CallNumbersItemDto } from './constants';

import css from './CallNumbersPage.module.scss';

export const CallNumbersPage: FC = () => {
  const { $t } = useIntl();
  const queryParams = useQueryParams();
  const [pageNumber, setPageNumber] = useState(1);
  const [callNumbersShelfKey, setCallNumbersShelfKey] = useState('');

  const callNumbersResource = useCallNumbersFetch(
    pageNumber,
    callNumbersShelfKey
  );

  useEffect(() => {
    setCallNumbersShelfKey((queryParams.search as string) || '');
    setPageNumber(1);
  }, [queryParams.search]);

  const loadMore = () => {
    setPageNumber(pageNumber + 1);
    setCallNumbersShelfKey(callNumbersResource.next ?? '');
  };

  const {
    documentTitle,
    callNumber,
    year,
    authors,
    pageTitle,
    emptyStateTitle,
    searchBarPlaceholder,
    showMoreButtonTitle,
    yourCallNumber,
    yourCallNumberWouldBeHere,
  } = callNumbersMessages;

  const getHighlightedCallNumber = useCallback(
    fullCallNumber => {
      return (
        <div className={css.highlightedCallNumberWrapper}>
          <FontAwesomeIcon
            icon={faBooksSolid}
            className={css.highlightedCallNumberIcon}
          />
          {fullCallNumber
            ? $t(yourCallNumber, {
                callNumber: fullCallNumber,
                b: chunks => (
                  <mark className={css.highlightedCallNumber}>{chunks}</mark>
                ),
              })
            : $t(yourCallNumberWouldBeHere, {
                callNumber: queryParams.search,
              })}
        </div>
      );
    },
    [$t, yourCallNumber, yourCallNumberWouldBeHere, queryParams.search]
  );

  const columns = useMemo<BrowseColumn<CallNumbersItemDto>[]>(
    () => [
      {
        label: $t(callNumber),
        formatter: ({ fullCallNumber, isAnchor }) =>
          isAnchor ? getHighlightedCallNumber(fullCallNumber) : fullCallNumber,
      },
      {
        label: $t(sharedMessages.title),
        formatter: ({ instance }) =>
          instance && (
            <div>
              <Link
                data-auto="instance-details-link"
                className="underlinedLink"
                to={{
                  pathname: generatePath(ROUTE.instanceDetails, {
                    id: instance.id,
                  }),
                }}
              >
                {instance.title}
              </Link>
            </div>
          ),
      },
      {
        label: $t(year),
        formatter: ({ instance }) =>
          instance?.publication?.[0].dateOfPublication,
      },
      {
        label: $t(authors),
        formatter: ({ instance }) => (
          <DelimitedList
            items={instance?.contributors}
            limit={MAX_CONTRIBUTORS_TO_DISPLAY}
            property="name"
          />
        ),
      },
    ],
    [authors, callNumber, $t, getHighlightedCallNumber, year]
  );

  const renderEmptyState = () => (
    <EmptyRecordsMessage
      className={css.emptyRecordsMessage}
      icon={faBooks}
      message={$t(emptyStateTitle)}
    />
  );

  const checkShouldRenderEmptyHighlightedRow = record =>
    record.isAnchor && !record.fullCallNumber;

  const renderEmptyHighlightedRow = ({ record, classes: browseClasses }) => (
    <div
      role="cell"
      className={cn(browseClasses.cell, css.emptyHighlightedCell)}
    >
      {columns[0].formatter(record)}
    </div>
  );

  return (
    <Browse
      messages={{
        documentTitle,
        pageTitle,
        searchBarPlaceholder,
        showMoreButtonTitle,
      }}
      resource={callNumbersResource}
      columns={compact(columns)}
      pageNumber={pageNumber}
      tableClasses={{ cell: css.tableCell }}
      isDataLoadedOnInitialRender={false}
      loadMore={loadMore}
      setPageNumber={setPageNumber}
      renderEmptyState={renderEmptyState}
      shouldRenderSpecialRow={checkShouldRenderEmptyHighlightedRow}
      renderSpecialRow={renderEmptyHighlightedRow}
    />
  );
};
